import React, { useState } from 'react';
import { X } from 'lucide-react';
import SatelliteMeasure from '../measurements/SatelliteMeasure';

/**
 * SatMeasureModal - Modal wrapper for satellite measurement tool
 * 
 * Used from JobFormModal/QuoteCalculator to quickly measure a property
 * and return the measurement value to populate the quote calculator.
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onMeasurementComplete: (measurement) => void - called with { totalSqFt, totalLinearFt, value }
 * - address: string - initial address to load
 * - customerName: string - for display
 * - serviceName: string - the service being measured (e.g., "House Wash")
 * - measurementType: 'sqft' | 'linear' - what type of measurement we need
 */
const SatMeasureModal = ({
  isOpen,
  onClose,
  onMeasurementComplete,
  address = '',
  customerName = '',
  serviceName = '',
  measurementType = 'sqft',
}) => {
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  // Handle save from SatelliteMeasure component
  const handleSave = async (measurementData) => {
    setIsSaving(true);
    
    try {
      // Extract the relevant measurement based on type needed
      const totalSqFt = measurementData.zones?.reduce((sum, z) => sum + (z.squareFeet || 0), 0) || 0;
      const totalLinearFt = measurementData.zones?.reduce((sum, z) => sum + (z.linearFeet || 0), 0) || 0;
      
      const result = {
        totalSqFt,
        totalLinearFt,
        zones: measurementData.zones || [],
        // Return the primary value based on measurement type
        value: measurementType === 'linear' ? totalLinearFt : totalSqFt,
      };
      
      // Call parent callback with measurement
      if (onMeasurementComplete) {
        onMeasurementComplete(result);
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving measurement:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={handleCancel}
      />
      
      {/* Modal */}
      <div className="relative w-full h-full md:w-[95vw] md:h-[90vh] md:max-w-6xl md:rounded-xl overflow-hidden bg-white shadow-2xl flex flex-col">
        {/* SatelliteMeasure Component in Quick Mode */}
        <div className="flex-1 overflow-hidden">
          <SatelliteMeasure
            customer={{ name: customerName }}
            initialAddress={address}
            onSave={handleSave}
            onCancel={handleCancel}
            existingMeasurement={null}
            serviceTypes={[]}
            readOnly={false}
            // Quick mode props
            quickMode={true}
            quickModeServiceName={serviceName}
            quickModeMeasurementType={measurementType}
          />
        </div>
      </div>
    </div>
  );
};

export default SatMeasureModal;